import React, { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { TYPE_LINK, useOfferLinkGetter, useOfferTags, usePromoOffers } from 'cityads-v3/hooks/offers';
import { Offer } from 'cityads-v3/types/offers';
import { PromoOffer } from 'cityads-v3/types/promoOffer';
import { RootState } from 'cityads-v3/types/reducer';
import { TableColumn } from 'cityads-v3/components/Table/TableTypes';
import { OffersListProps } from './OffersList';
import classes from './Offers.scss';
import promoClasses from './PromoOffers.scss';

import Box from '@mui/material/Box';
import DateFormat from 'cityads-v3/components/Format/DateFormat';
import FavoriteButton from './FavoriteButton';
import GetLinkButton from '../offerPage/GetLinkButton';
import ImgNSWithFallback from 'cityads-v3/components/ImgWithFallback/ImgNSWithFallback';
import PaidFormat from 'cityads-v3/components/Format/PaidFormat'
import TargetValueFormat from 'cityads-v3/components/Format/TargetValueFormat';
import Tooltip from 'cityads-v3/components/Tooltip';
import Typography from '@mui/material/Typography';

import {
  ACTION_STATUS_WORKING,
  ACTION_TYPE_DIRECTION_ID_API,
} from 'cityads-v3/reducers/offers';
import { FORMAT_CURRENCY } from 'cityads-v3/components/Format/NumberFormat';
import { OfferWithPromoAndStats } from './OffersListTable';
import { useThemeChanger } from 'cityads-v3/hooks/theme';

const TABLE_FIELDS_MOBILE = ['getLink', 'setFav', 'name', 'paid'];

/**
 * Дополнение списка офферов стилями
 * для красочного отображения строк в таблице
 */
export function useOffersWithPromoStyles(offers: Offer[]) {
  const { theme } = useThemeChanger()
  const promoOffersInfo = usePromoOffers() as Record<Offer['id'], PromoOffer>;
  const [, offerTagsByCode] = useOfferTags();

  return useMemo(() => offers.map(offer => {
    const item: OfferWithPromoAndStats = { ...offer };
    const promo = promoOffersInfo?.[item.id];
    const tagColors = item.tags
      .sort((code1, code2) => offerTagsByCode[code1]?.id - offerTagsByCode[code2]?.id)
      .map(code => offerTagsByCode[code]?.isVisible ? offerTagsByCode[code]?.offer_bg_color : null)
      .filter(color => !!color)
      ;

    item._style = {
      backgroundColor: promo?.bg_color || tagColors[tagColors.length - 1] || null,
      '--promo-color': (promo?.bg_color || null),
      '--promo-image': (promo?.bg_image ? `url(${promo.bg_image[0]?.source})` : null),
    };

    if (theme.palette.mode === 'dark') { // @todo: придумать решение для темной темы
      item._style = {}
    }

    if (promo) {
      item.promo = promo;
      item.classes = {
        root: promoClasses.tableRow,
      };
    }

    return {
      ...offer,
      ...item
    }
  }), [offers, promoOffersInfo, offerTagsByCode])

}


/**
* Столбцы таблицы списка офферов
*/
export function useOfferTableCols(
  reloadFavoriteTotalCount: OffersListProps['reloadFavoriteTotalCount'],
  linkSource: OffersListProps['linkSource']
): TableColumn<OfferWithPromoAndStats>[] {
  const { t } = useTranslation(['Offers']);
  const user = useSelector((state: RootState) => state.auth.user);
  const isMobile = useSelector((state: RootState) => state.app.isMobile);
  const linkGetter = useOfferLinkGetter();
  const getOfferLink = useCallback((id: Offer['id']) => linkGetter(
    id,
    linkSource
  ), [linkGetter, linkSource])

  return useMemo(() => {
    const columns: TableColumn<OfferWithPromoAndStats>[] = []

    if (user) {
      // Получение ссылки
      columns.push({
        name: 'getLink',
        title: '',
        dense: true,
        padding: 'none',
        formatter: (value, row) => (
          <>
            <GetLinkButton
              id={row.id}
              view="icon"
              iconButtonProps={{
                size: 'small',
                sx: { p: '4px' },
              }}
            />

            {(row.promo?.has_title && row.promo?.title) ? <div className={promoClasses.tableBadge}>{row.promo.translated_title || row.promo.title}</div> : null}
          </>
        )

      })
    }

    // Избранное
    columns.push({
      name: 'setFav',
      title: '',
      dense: true,
      padding: 'none',
      formatter: (value, row) => (
        <>
          <FavoriteButton
            offerId={row.id}
            size="small"
            onComplete={reloadFavoriteTotalCount}
            sx={{ p: '4px' }}
          />
          {(!user && row.promo?.has_title && row.promo?.title) ? <div className={promoClasses.tableBadge}>{row.promo.translated_title || row.promo.title}</div> : null}
        </>
      )
    })

    // ID оффера
    columns.push({
      name: 'id',
      title: t('table.id'),
      dense: true,
      padding: 'none',
      formatter: (value) => (
        <Box sx={{ p: '4px' }}>{value}</Box>
      )
    })

    // Favicon
    columns.push({
      name: 'favicon',
      title: '',
      dense: true,
      padding: 'none',
      formatter: (value, row) => {
        const littleFavicon = row?.favicon?.[0]?.source || null;

        return <ImgNSWithFallback
          className={classes.offerFavicon}
          src={littleFavicon}
          fallbackSrc={require('./img/default_avatar.svg')}
          alt={row.name}
          width="16"
          height="16"
        />;
      }
    })

    // Имя оффера
    columns.push({
      name: 'name',
      title: t('table.name'),
      formatter: (value, row) => {
        const [link, linkType] = getOfferLink(row.id);
        const name = row.translated_name ?? row.name;
        return linkType === TYPE_LINK
          ? <Typography component={Link} to={link} variant="body2" color="textPrimary">{name}</Typography>
          : <Typography component="a" href={link.toString()} variant="body2" color="textPrimary">{name}</Typography>
      },
      getClassName: row => row?.promo ? promoClasses.tableCell : null,
    })

    // Дата запуска
    columns.push({
      name: 'date_start',
      title: t('table.date_start'),
      formatter: (value, row) => {
        const { status, planned_start_date } = row;

        if (status === ACTION_STATUS_WORKING) {
          return <DateFormat date={value} />;
        }

        if (!planned_start_date) {
          return '—';
        }

        return (
          <Tooltip title={t('table.date_planned_start')} enabled>
            <abbr><DateFormat date={planned_start_date} /></abbr>
          </Tooltip>
        );
      }
    })

    // Выплата
    columns.push({
      name: 'paid',
      title: t('table.paid'),
      formatter: (value, row) => {
        return <PaidFormat commissionData={row.commission_data} precision={2} />
      }
    })

    columns.push({
      name: 'cr',
      title: t('table.cr'),
      format: 'percent',
      precision: 2,
      tooltip: t('table.crTooltip'),
      formatter: value => value * 100
    })

    columns.push({
      name: 'ar',
      title: t('table.ar'),
      format: 'percent',
      precision: 0,
      tooltip: t('table.arTooltip'),
      formatter: value => value ? value * 100 : null
    })

    columns.push({
      name: 'ecpc_ecpl',
      title: t('table.ecpc_ecpl'),
      tooltip: t('table.ecpc_ecplTooltip'),
      formatter: (value, { action_type_direction_id, ecpc, ecpl, currency_code, original_ecpc, original_ecpl, original_currency_code }) => {
        const isApi = Number(action_type_direction_id) === ACTION_TYPE_DIRECTION_ID_API;
        const ecplEcpc = isApi ? ecpl : ecpc;
        const originalEcplEcpc = isApi ? original_ecpl : original_ecpc;

        return (
          <TargetValueFormat
            format={FORMAT_CURRENCY}
            currency={currency_code}
            min={ecplEcpc}
            max={ecplEcpc}
            originalCurrency={original_currency_code}
            originalMin={originalEcplEcpc}
            originalMax={originalEcplEcpc}
          />
        );
      },
    })

    return columns.filter(item => {
      if (isMobile) {
        return TABLE_FIELDS_MOBILE.indexOf(item.name) !== -1;
      }
      return true;
    });
  }, [
    t,
    user,
    isMobile,
    linkGetter
  ])
}
